import { useMemo } from "react";
import { DocumentTransformation, ExtractedRecord, TargetSchema } from "@/types";
import { getRecordValidationErrors } from "../utils/recordUtils";
import { groupRecordsBySchema, summarizeRecords } from "../utils/recordSummary";

interface UseReviewRecordsArgs {
  transformation: DocumentTransformation | null;
  /**
   * Records currently in hand. For a document small enough to load whole this
   * is every record; for a large one it is the pages loaded so far, in
   * record_index order (#6755).
   */
  records: ExtractedRecord[] | undefined;
}

/**
 * Organise extracted records for the review surface.
 *
 * Takes `records` explicitly rather than reading transformation.extracted_records,
 * because the review tab no longer fetches the whole set — at 497,711 records
 * that request kills mgmt-api (#6826).
 *
 * COUNTS AND ROWS COME FROM DIFFERENT PLACES, deliberately. Rows are whatever is
 * loaded. Counts come from the server's `processing_metadata.record_summary`,
 * computed in the worker over the full set, so a tab badge reads 497,710 while
 * the grid shows the first page. Falling back to counting the loaded rows would
 * silently under-report — a reviewer would see "200 claim lines" on a document
 * with half a million, which is worse than useless on a claims review surface.
 *
 * The fallback to client-side counting is only for transformations written
 * before the summary existed, where the whole set is loaded anyway.
 */
export const useReviewRecords = ({ transformation, records }: UseReviewRecordsArgs) => {
  // Group into per-schema buckets, deduping claimants to first occurrence.
  // Applied to the loaded prefix this IS streaming dedup: pages arrive in
  // record_index order, so the first occurrence of a member_id in the prefix is
  // its first occurrence in the document.
  const recordsBySchema = useMemo(() => groupRecordsBySchema(records), [records]);

  const serverSummary = transformation?.processing_metadata?.record_summary;

  /** Whole-set counts: server summary when present, else derived from records. */
  const summary = useMemo(() => serverSummary ?? summarizeRecords(records), [serverSummary, records]);

  // Available schemas come off the transformation row, which needs no records
  // at all — this already worked before #6755 and is why the tab can decide
  // what to render before a single page arrives.
  const { availableSchemas, recordCounts } = useMemo(() => {
    const schemas =
      transformation?.target_schemas && transformation.target_schemas.length > 0
        ? transformation.target_schemas
        : (Object.keys(recordsBySchema) as TargetSchema[]);

    const counts: Record<TargetSchema, number> = {} as Record<TargetSchema, number>;
    schemas.forEach((schema) => {
      counts[schema] = summary.counts_by_schema[schema] ?? 0;
    });

    return { availableSchemas: schemas, recordCounts: counts };
  }, [recordsBySchema, transformation, summary]);

  const totalRecords = summary.total_records;

  // Records needing review. The transformation-level "any validation_errors at
  // all" flag makes EVERY record count, which is why it stays here rather than
  // in the server summary: the server would have to know the transformation's
  // own errors, and this side already has them.
  const recordsNeedingReview = useMemo(() => {
    const transformationHasErrors = (transformation?.validation_errors?.length || 0) > 0;
    if (transformationHasErrors) return summary.total_records;
    if (serverSummary) return serverSummary.records_with_errors;
    // DISTINCT records, mirroring the server's records_with_errors semantics
    // (#6755 review round 2) — flattening the buckets double-counted a
    // two-schema record. Set-dedup by reference is sound here: bucket entries
    // are the same objects the records array holds.
    return new Set(
      Object.values(recordsBySchema)
        .flat()
        .filter((r) => getRecordValidationErrors(r).length > 0)
    ).size;
  }, [recordsBySchema, transformation, summary, serverSummary]);

  // Check if this is a claims document that requires a client
  const requiresClient = useMemo(() => {
    return availableSchemas.some((schema) =>
      [
        TargetSchema.STOPLOSS_CLAIM,
        TargetSchema.CLAIM_ITEM,
        TargetSchema.CLAIMANT,
        TargetSchema.REIMBURSEMENT,
      ].includes(schema)
    );
  }, [availableSchemas]);

  return {
    recordsBySchema,
    availableSchemas,
    recordCounts,
    totalRecords,
    recordsNeedingReview,
    requiresClient,
    hasRecords: availableSchemas.length > 0,
  };
};
